import { useCallback } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { showErrorNotification, showWarningNotification } from '../../../widgets/notificationToaster';
import {
    ServiceAvailabilityResult,
    serviceAvailabilityStatusQuery,
    ServicesWithoutCredits
} from '../queries/__generated__/serviceAvailabilityStatusQuery.graphql';
import { ServiceAvailabilityStatusQuery } from '../queries/serviceAvailabilityStatus';
import { ToolType } from '../types';

const SERVICE_MAP: Partial<Record<ToolType, ServicesWithoutCredits>> = {
    'virtual-tryon': 'AI_STUDIO_VTO',
    'magic-swap': 'AI_STUDIO_MAGIC_SWAP',
    'multi-image-fusion': 'AI_STUDIO_MULTI_IMAGE_FUSION',
    'ad-generator': 'AI_STUDIO_AD_GENERATOR',
    'video-generation': 'AI_STUDIO_VIDEO_GENERATOR',
    upscaler: 'AI_STUDIO_UPSCALER',
    'product-staging': 'AI_STUDIO_PRODUCT_STAGING',
    'multi-views-to-3d': 'AI_STUDIO_3D_MODEL_GENERATOR',
    'text-to-3d': 'AI_STUDIO_3D_MODEL_GENERATOR',
    'image-to-3d': 'AI_STUDIO_3D_MODEL_GENERATOR'
};

/** Maps a non-AVAILABLE non-ADDON_NOT_ACTIVE status to the warning shown in the toast. */
function getStatusWarningMessage(status: ServiceAvailabilityResult): string | null {
    switch (status) {
        case 'TRIAL_NOT_ALLOWED':
            return T._('AI Agents Studio is not available during the trial period. Purchasing your plan to access AI Agents Studio.', 'Admin');
        case 'SHOPIFY_DEV_STORE_NOT_ALLOWED':
            return T._('AI Agents Studio is not available for Shopify development stores. Use a real store to access AI Agents Studio.', 'Admin');
        case 'USE_LIMIT_EXCEEDED':
            return T._('You have reached the usage limit for this AI Agents Studio service. Please try again later.', 'Admin');
        case 'NO_CREDITS':
            return T._('You do not have enough credits to use this AI Agents Studio service. Please add credits to continue.', 'Admin');
        case 'FEATURE_NOT_INCLUDED_IN_PLAN':
            return T._('This AI Agents Studio feature is not included in your current plan. Upgrade your plan to access it.', 'Admin');
        default:
            return null;
    }
}

export interface PreflightOptions {
    /** Fired synchronously when the AI_STUDIO addon is missing and an activation flow has been triggered. Useful to show optimistic UI. */
    onAddonActivationStarted?: () => void;
    /** Override for USE_LIMIT_EXCEEDED. If provided, suppresses the default toast (e.g. to show a fair-use dialog instead). */
    onLimitReached?: (serviceName: string) => void;
    /** Fired when the availability check could not be performed (network/server error). */
    onCheckFailed?: () => void;
    /** Fired when the availability check returned a non-AVAILABLE status that aborts generation. Receives the raw enum. */
    onUnavailable?: (status: ServiceAvailabilityResult) => void;
}

export interface UseGenerationPreflightReturn {
    /**
     * Ensures the AI_STUDIO addon is active and the per-tool service is available before invoking onReady.
     * If the server reports ADDON_NOT_ACTIVE, the activation dialog is opened and onReady runs only on successful activation.
     * Other non-AVAILABLE statuses surface a warning toast and abort.
     */
    runWithPreflight: (toolType: ToolType, onReady: () => void | Promise<void>, options?: PreflightOptions) => Promise<void>;
}

export function useGenerationPreflight(): UseGenerationPreflightReturn {
    const relayEnvironment = useRelayEnvironment();
    const { activateAddonForSeller, updateSubscription } = useSubscriptionData();

    const fetchStatus = useCallback(
        async (serviceType: ServicesWithoutCredits): Promise<ServiceAvailabilityResult | null> => {
            try {
                const data = await fetchQuery<serviceAvailabilityStatusQuery>(relayEnvironment, ServiceAvailabilityStatusQuery, {
                    serviceWithoutCredits: serviceType
                }).toPromise();
                return data?.serviceAvailabilityStatus ?? null;
            } catch (error) {
                console.error('Failed to check AI service availability:', error);
                showErrorNotification({ message: T._('Failed to check AI service availability.', 'Admin') });
                return null;
            }
        },
        [relayEnvironment]
    );

    const runWithPreflight = useCallback(
        async (toolType: ToolType, onReady: () => void | Promise<void>, options: PreflightOptions = {}) => {
            const serviceType = SERVICE_MAP[toolType];
            // Tools without a mapped service skip the server-side check.
            if (!serviceType) {
                await onReady();
                return;
            }

            const status = await fetchStatus(serviceType);
            if (!status) {
                options.onCheckFailed?.();
                return;
            }

            if (status === 'AVAILABLE') {
                await onReady();
                return;
            }

            if (status === 'ADDON_NOT_ACTIVE') {
                options.onAddonActivationStarted?.();
                activateAddonForSeller(
                    'AI_STUDIO',
                    'USD',
                    () => {
                        // Refresh the Relay cache so subsequent checks see the activated addon.
                        updateSubscription();
                        // Re-run the preflight: any other constraint (trial, dev store, credits, etc.)
                        // could still apply even after the addon is active.
                        void runWithPreflight(toolType, onReady, options);
                    },
                    false
                );
                return;
            }

            options.onUnavailable?.(status);

            // USE_LIMIT_EXCEEDED has a custom UI hook (e.g. fair-use dialog). When provided, suppress the toast.
            if (status === 'USE_LIMIT_EXCEEDED' && options.onLimitReached) {
                options.onLimitReached(toolType);
                return;
            }

            const message = getStatusWarningMessage(status);
            if (message) showWarningNotification({ message });
        },
        [fetchStatus, activateAddonForSeller, updateSubscription]
    );

    return { runWithPreflight };
}
